import React, { useEffect, useRef, useState } from 'react';
import { API } from '../services/api';
import './AverageOutage.css';

const timeWindowLabels = {
  '1': '11am-11:30am',
  '2': '11:30am-12pm',
  '3': '12pm-12:30pm',
  '4': '12:30pm-1pm',
  '5': '1pm-1:30pm',
  '6': '1:30pm-2pm',
  '7': '2pm-2:30pm',
  '8': '2:30pm-3pm',
  '9': '3pm-3:30pm',
  '10': '3:30pm-4pm',
  '11': '4pm-4:30pm',
  '12': '4:30pm-5pm',
  '13': '5pm-5:30pm',
  '14': '5:30pm-6pm',
  '15': '6pm-6:30pm',
  '16': '6:30pm-7pm',
  '17': '7pm-7:30pm',
  '18': '7:30pm-8pm',
  '19': '8pm-8:30pm',
  '20': '8:30pm-9pm',
};

const timeWindowIds = Object.keys(timeWindowLabels);

function AverageOutage({ store: propStore = null }) {
  const scrollContainerRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(propStore || 'all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    API.getStores()
      .then(storeList => {
        setStores(storeList);
      })
      .catch(err => {
        console.error('Error loading stores:', err);
      });
  }, []);

  useEffect(() => {
    setSelectedStore(propStore || 'all');
  }, [propStore]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    if (startDate > endDate) {
      setError('Start date must be on or before end date.');
      setTableData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const storeFilter = selectedStore === 'all' ? null : selectedStore;

    API.getAverageOutage(storeFilter, startDate, endDate)
      .then(data => {
        const itemMap = {};

        data.forEach(record => {
          const itemName = record.item_name || record.item_id;
          const timeWindowId = String(record.time_window_id);
          const outagePercentage = parseFloat(record.outage_percentage);
          const inStockPercentage = Number.isFinite(outagePercentage)
            ? Math.max(0, Math.min(100, 100 - outagePercentage))
            : null;

          if (!itemMap[itemName]) {
            itemMap[itemName] = {};
          }

          itemMap[itemName][timeWindowId] = inStockPercentage;
        });

        const tableRows = Object.keys(itemMap).map(itemName => ({
          itemName,
          timeWindows: itemMap[itemName],
        }));

        setTableData(tableRows);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading average outage data:', err);
        setError('Unable to load average outage data.');
        setTableData([]);
        setLoading(false);
      });
  }, [selectedStore, startDate, endDate]);

  const getCellClass = (value) => {
    if (value === null || value === undefined) return 'average-outage-unknown';
    if (value >= 100) return 'average-outage-100';
    if (value >= 75) return 'average-outage-75';
    if (value >= 50) return 'average-outage-50';
    if (value > 0) return 'average-outage-25';
    return 'average-outage-0';
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '';
    return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
  };

  const handleMouseDown = (event) => {
    if (event.button !== 0 || !scrollContainerRef.current) return;

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: scrollContainerRef.current.scrollLeft,
      scrollTop: scrollContainerRef.current.scrollTop,
    };
    setIsDragging(true);
  };

  const handleMouseMove = (event) => {
    const { isDragging, startX, startY, scrollLeft, scrollTop } = dragStateRef.current;
    if (!isDragging || !scrollContainerRef.current) return;

    event.preventDefault();
    scrollContainerRef.current.scrollLeft = scrollLeft - (event.clientX - startX);
    scrollContainerRef.current.scrollTop = scrollTop - (event.clientY - startY);
  };

  const stopDragging = () => {
    dragStateRef.current.isDragging = false;
    setIsDragging(false);
  };

  if (loading) {
    return <div className="component-loading">Loading average outage data...</div>;
  }

  return (
    <div className="average-outage">
      <div className="average-outage-header">
        <h2>Average Outage</h2>
        <div className="average-outage-controls">
          <label>
            Store:
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="store-selector"
            >
              <option value="all">All Stores</option>
              {stores.map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
          </label>
          <label>
            Start Date:
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-selector"
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-selector"
            />
          </label>
        </div>
      </div>
      <p className="average-outage-note">
        Percentages show how often each item was in stock during the selected date range.
      </p>

      {error ? (
        <div className="no-data">{error}</div>
      ) : tableData.length > 0 ? (
        <div
          ref={scrollContainerRef}
          className={`average-outage-table-container ${isDragging ? 'is-dragging' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
        >
          <div className="average-outage-table-scroll">
            <table className="average-outage-table">
              <thead>
                <tr>
                  <th className="item-name-col">Item</th>
                  {timeWindowIds.map((timeWindowId) => (
                    <th key={timeWindowId} className="time-window-col">
                      {timeWindowLabels[timeWindowId]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row) => (
                  <tr key={row.itemName}>
                    <td className="item-name-cell">{row.itemName}</td>
                    {timeWindowIds.map((timeWindowId) => {
                      const value = row.timeWindows[timeWindowId];

                      return (
                        <td
                          key={timeWindowId}
                          className={`average-outage-cell ${getCellClass(value)}`}
                        >
                          {formatPercentage(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="average-outage-legend">
            <span className="legend-item">
              <span className="legend-swatch average-outage-0"></span>
              0% in stock
            </span>
            <span className="legend-item">
              <span className="legend-swatch average-outage-25"></span>
              1% - 49.9% in stock
            </span>
            <span className="legend-item">
              <span className="legend-swatch average-outage-50"></span>
              50% - 74.9% in stock
            </span>
            <span className="legend-item">
              <span className="legend-swatch average-outage-75"></span>
              75% - 99.9% in stock
            </span>
            <span className="legend-item">
              <span className="legend-swatch average-outage-100"></span>
              100% in stock
            </span>
            <span className="legend-item">
              <span className="legend-swatch average-outage-unknown"></span>
              No Data
            </span>
          </div>
        </div>
      ) : (
        <div className="no-data">No outage data available for the selected filters</div>
      )}
    </div>
  );
}

export default AverageOutage;
